import { Box, Typography, Divider } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { motion } from 'framer-motion';
import { useBookingSectionValidation } from '../../../hooks/useBookingSectionValidation';
import {
  bookingReviewSchema,
  type ReviewData,
  type CouponValidationResult,
  type FoodItemSelection,
} from '@lov/shared';
import { useEffect, useRef, useMemo } from 'react';
import { useAvailableAddons } from '../../../hooks/useAvailableAddons';
import { CouponSection } from '../../CouponSection';
import { useTheme } from '@mui/material/styles';
import { alpha } from '@mui/material/styles';

const noop = () => {
  /* no-op fallback */
};

interface ReviewSectionProps {
  bookingData: {
    venueId?: string;
    venueName?: string;
    date?: string;
    startTime?: string;
    endTime?: string;
    durationMinutes?: number;
    basePrice?: number; // Venue base price (per hour)
    guestCount?: number;
    serviceFeePercent?: number; // From admin settings
    gstRate?: number; // From admin settings
    gstEnabled?: boolean; // Whether GST is enabled
    venueInventoryId?: string; // For fetching addon details
    selectedAddons?: Array<{
      addonItemId: string;
      venueInventoryId: string;
      quantity: number;
      name?: string;
      pricePerHour?: number;
      pricePerHalfHour?: number;
    }>;
    bookingMode?: 'capacity' | 'inventory';
    selectedInventory?: Array<{
      venueInventoryId: string;
      itemName: string;
      quantity: number;
      pricePerHour: number;
    }>;
    selectedFoodItems?: FoodItemSelection[];
  };
  appliedCoupon?: CouponValidationResult | null;
  onApplyCoupon?: (result: CouponValidationResult) => void;
  onRemoveCoupon?: () => void;
  onComplete: (data: { totalPrice: number; couponCode?: string }, summary: string) => void;
  registerContinue?: (callback: () => void) => void;
}

export function ReviewSection({
  bookingData,
  appliedCoupon,
  onApplyCoupon,
  onRemoveCoupon,
  onComplete,
  registerContinue,
}: ReviewSectionProps) {
  const theme = useTheme();
  const { validateAndProceed, shake } =
    useBookingSectionValidation<ReviewData>(bookingReviewSchema);

  // Fetch addon details if addons are selected
  const { addons, durationHours: addonDurationHours } = useAvailableAddons(
    bookingData.venueInventoryId || null,
    bookingData.date || null,
    bookingData.startTime || null,
    bookingData.endTime || null,
    { enablePolling: false }
  );

  // Calculate pricing
  const durationHours = (bookingData.durationMinutes || 60) / 60;
  const isInventoryMode = bookingData.bookingMode === 'inventory';

  const inventorySubtotal = useMemo(() => {
    if (!isInventoryMode || !bookingData.selectedInventory?.length) return 0;
    return bookingData.selectedInventory.reduce((total, item) => {
      return total + Math.round(item.pricePerHour * item.quantity * durationHours);
    }, 0);
  }, [isInventoryMode, bookingData.selectedInventory, durationHours]);

  const durationSlots = (bookingData.durationMinutes || 60) / 30;
  const bookingBasePrice = isInventoryMode
    ? inventorySubtotal
    : Math.round((bookingData.basePrice || 0) * durationSlots);

  // Calculate addon subtotal
  // Use addon API data when available, fall back to embedded pricing from inventory availability
  const addonSubtotal = useMemo(() => {
    if (!bookingData.selectedAddons || bookingData.selectedAddons.length === 0) return 0;

    return bookingData.selectedAddons.reduce((total, selectedAddon) => {
      const addon = addons.find((a) => a.addonItemId === selectedAddon.addonItemId);

      if (addon) {
        // Use addon API data (has pricePerUnit and pricingModel)
        let itemTotal = addon.pricePerUnit * selectedAddon.quantity;
        if (addon.pricingModel === 'HOURLY') {
          itemTotal *= addonDurationHours;
        } else if (addon.pricingModel === 'HALF_HOURLY') {
          itemTotal *= addonDurationHours * 2;
        }
        return total + itemTotal;
      }

      // Fallback: use embedded pricing from inventory availability
      const pricePerHour = selectedAddon.pricePerHour;
      const pricePerHalfHour = selectedAddon.pricePerHalfHour;
      if (pricePerHour != null) {
        return total + pricePerHour * selectedAddon.quantity * durationHours;
      }
      if (pricePerHalfHour != null) {
        return total + pricePerHalfHour * selectedAddon.quantity * durationHours * 2;
      }
      return total;
    }, 0);
  }, [bookingData.selectedAddons, addons, addonDurationHours, durationHours]);

  // Calculate food subtotal
  const foodSubtotal = useMemo(() => {
    if (!bookingData.selectedFoodItems?.length) return 0;
    const total = bookingData.selectedFoodItems.reduce(
      (sum, item) => sum + (item.unitPrice || 0) * (item.quantity || 0),
      0
    );
    return isNaN(total) ? 0 : total;
  }, [bookingData.selectedFoodItems]);

  const serviceFeePercent = bookingData.serviceFeePercent || 0;
  const gstRate = bookingData.gstRate || 18;

  // Coupon discount must be computed before service fee (service fee is applied on post-discount amount)
  const couponDiscount = appliedCoupon?.isValid ? (appliedCoupon.discountAmount ?? 0) : 0;

  // Service fee is computed on the discounted subtotal (food included in base for service fee calc)
  const afterDiscount = bookingBasePrice + addonSubtotal + foodSubtotal - couponDiscount;
  const serviceFee = Math.round(afterDiscount * (serviceFeePercent / 100));

  // GST on service fee — always applies when service fee is charged
  const gst = serviceFee > 0 && gstRate > 0 ? Math.round(serviceFee * (gstRate / 100)) : 0;

  const totalPrice = Math.max(
    0,
    bookingBasePrice + addonSubtotal + foodSubtotal + serviceFee + gst - couponDiscount
  );

  const completedRef = useRef(false);
  const onCompleteRef = useRef(onComplete);

  // Keep onCompleteRef current
  useEffect(() => {
    onCompleteRef.current = onComplete;
  });

  // Auto-complete whenever pricing (or coupon) changes
  useEffect(() => {
    if (totalPrice >= 0) {
      completedRef.current = true;
      const couponCode = appliedCoupon?.isValid ? appliedCoupon.coupon?.code : undefined;
      onCompleteRef.current({ totalPrice, couponCode }, `₹${totalPrice.toLocaleString('en-IN')}`);
    }
  }, [totalPrice, appliedCoupon]);

  const handlePayment = () => {
    validateAndProceed(() => {
      // Validation passed, onComplete already called via useEffect above
      // This just ensures the section is marked complete if validation is needed
      if (!completedRef.current) {
        const couponCode = appliedCoupon?.isValid ? appliedCoupon.coupon?.code : undefined;
        onCompleteRef.current({ totalPrice, couponCode }, `₹${totalPrice.toLocaleString('en-IN')}`);
      }
    });
  };

  useEffect(() => {
    if (registerContinue) {
      registerContinue(handlePayment);
    }
  }, [registerContinue, handlePayment]);

  return (
    <motion.div animate={shake ? { x: [-10, 10, -10, 10, 0] } : {}} transition={{ duration: 0.5 }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <Typography variant="h6">Booking Summary</Typography>

        {/* Booking details */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 1 }}>
          {bookingData.venueName && (
            <Typography variant="body2" color="text.secondary">
              <strong>Venue:</strong> {bookingData.venueName}
            </Typography>
          )}
          {bookingData.date && (
            <Typography variant="body2" color="text.secondary">
              <strong>Date:</strong>{' '}
              {new Date(bookingData.date).toLocaleDateString('en-IN', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
              })}
            </Typography>
          )}
          {bookingData.startTime && bookingData.endTime && (
            <Typography variant="body2" color="text.secondary">
              <strong>Time:</strong> {bookingData.startTime} - {bookingData.endTime} (
              {durationHours}h)
            </Typography>
          )}
          {bookingData.guestCount && (
            <Typography variant="body2" color="text.secondary">
              <strong>Guests:</strong> {bookingData.guestCount}
            </Typography>
          )}
        </Box>

        {/* Pricing breakdown */}
        <Box sx={{ bgcolor: 'action.hover', p: 2, borderRadius: 2 }}>
          {isInventoryMode ? (
            <Box sx={{ mb: 1 }}>
              <Typography variant="body2" fontWeight={500} color="text.secondary" sx={{ mb: 1 }}>
                Equipment
              </Typography>
              {bookingData.selectedInventory?.map((item) => (
                <Box
                  key={item.venueInventoryId}
                  sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}
                >
                  <Typography variant="body2" color="text.secondary">
                    {item.itemName} x{item.quantity} @ ₹{item.pricePerHour}/h &times;{' '}
                    {durationHours}h
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    ₹
                    {Math.round(item.pricePerHour * item.quantity * durationHours).toLocaleString(
                      'en-IN'
                    )}
                  </Typography>
                </Box>
              ))}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  borderTop: '1px solid',
                  borderColor: 'divider',
                  pt: 1,
                  mt: 1,
                }}
              >
                <Typography variant="body2" fontWeight={500}>
                  Equipment Subtotal
                </Typography>
                <Typography variant="body2" fontWeight={500}>
                  ₹{inventorySubtotal.toLocaleString('en-IN')}
                </Typography>
              </Box>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography>
                Base Price ({durationHours}h @ ₹{bookingData.basePrice}/30min)
              </Typography>
              <Typography>₹{bookingBasePrice.toLocaleString('en-IN')}</Typography>
            </Box>
          )}

          {/* Add-ons line items */}
          {bookingData.selectedAddons && bookingData.selectedAddons.length > 0 && (
            <Box sx={{ mb: 1 }}>
              <Typography variant="body2" fontWeight={500} color="text.secondary" sx={{ mb: 0.5 }}>
                Add-ons
              </Typography>
              {bookingData.selectedAddons.map((selectedAddon) => {
                const addon = addons.find((a) => a.addonItemId === selectedAddon.addonItemId);

                // Derive pricing from addon API or embedded data
                let addonName: string;
                let itemTotal: number;
                let priceLabel: string;

                if (addon) {
                  addonName = addon.name;
                  itemTotal = addon.pricePerUnit * selectedAddon.quantity;
                  if (addon.pricingModel === 'HOURLY') {
                    itemTotal *= addonDurationHours;
                    priceLabel = `₹${addon.pricePerUnit}/h × ${addonDurationHours}h`;
                  } else if (addon.pricingModel === 'HALF_HOURLY') {
                    itemTotal *= addonDurationHours * 2;
                    priceLabel = `₹${addon.pricePerUnit}/30min × ${addonDurationHours}h`;
                  } else {
                    priceLabel = `₹${addon.pricePerUnit}/fixed`;
                  }
                } else if (selectedAddon.pricePerHour != null) {
                  addonName = selectedAddon.name || 'Add-on';
                  itemTotal = selectedAddon.pricePerHour * selectedAddon.quantity * durationHours;
                  priceLabel = `₹${selectedAddon.pricePerHour}/h × ${durationHours}h`;
                } else if (selectedAddon.pricePerHalfHour != null) {
                  addonName = selectedAddon.name || 'Add-on';
                  itemTotal =
                    selectedAddon.pricePerHalfHour * selectedAddon.quantity * durationHours * 2;
                  priceLabel = `₹${selectedAddon.pricePerHalfHour}/30min × ${durationHours}h`;
                } else {
                  return null;
                }

                return (
                  <Box
                    key={selectedAddon.addonItemId}
                    sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      {addonName} x{selectedAddon.quantity} @ {priceLabel}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      ₹{Math.round(itemTotal).toLocaleString('en-IN')}
                    </Typography>
                  </Box>
                );
              })}
              {addonSubtotal > 0 && (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    borderTop: '1px solid',
                    borderColor: 'divider',
                    pt: 0.5,
                    mt: 0.5,
                  }}
                >
                  <Typography variant="body2" fontWeight={500}>
                    Add-ons Subtotal
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    ₹{Math.round(addonSubtotal).toLocaleString('en-IN')}
                  </Typography>
                </Box>
              )}
            </Box>
          )}

          {/* Food items line items */}
          {bookingData.selectedFoodItems && bookingData.selectedFoodItems.length > 0 && (
            <Box sx={{ mb: 1 }}>
              <Typography variant="body2" fontWeight={500} color="text.secondary" sx={{ mb: 0.5 }}>
                Food & Drinks
              </Typography>
              {bookingData.selectedFoodItems.map((item) => (
                <Box
                  key={item.foodItemId}
                  sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}
                >
                  <Typography variant="body2" color="text.secondary">
                    {item.name} x{item.quantity} @ ₹{item.unitPrice}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    ₹{(item.unitPrice * item.quantity).toLocaleString('en-IN')}
                  </Typography>
                </Box>
              ))}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  borderTop: '1px solid',
                  borderColor: 'divider',
                  pt: 0.5,
                  mt: 0.5,
                }}
              >
                <Typography variant="body2" fontWeight={500}>
                  Food Subtotal
                </Typography>
                <Typography variant="body2" fontWeight={500}>
                  ₹{foodSubtotal.toLocaleString('en-IN')}
                </Typography>
              </Box>
            </Box>
          )}

          {/* Always show Service Fee and GST transparently */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography>
              Service Fee {serviceFeePercent > 0 ? `(${serviceFeePercent}%)` : ''}
            </Typography>
            <Typography color={serviceFee === 0 ? 'success.main' : undefined}>
              {serviceFee === 0 ? 'Free' : `₹${serviceFee.toLocaleString('en-IN')}`}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography>GST ({gstRate}% on service fee)</Typography>
            <Typography>₹{gst.toLocaleString('en-IN')}</Typography>
          </Box>

          <Divider sx={{ my: 1 }} />

          {/* Coupon discount line if applied */}
          {couponDiscount > 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography color="success.main">Coupon ({appliedCoupon?.coupon?.code})</Typography>
              <Typography color="success.main">
                -₹{couponDiscount.toLocaleString('en-IN')}
              </Typography>
            </Box>
          )}

          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography fontWeight={600}>Total</Typography>
            <Typography fontWeight={600}>₹{totalPrice.toLocaleString('en-IN')}</Typography>
          </Box>
        </Box>

        {/* Coupon input section */}
        {bookingData.venueId && bookingData.date && (
          <CouponSection
            venueId={bookingData.venueId}
            bookingDate={bookingData.date}
            startTime={bookingData.startTime || ''}
            endTime={bookingData.endTime || ''}
            subtotal={bookingBasePrice + addonSubtotal + foodSubtotal + serviceFee + gst}
            appliedCoupon={appliedCoupon ?? null}
            onApplyCoupon={onApplyCoupon ?? noop}
            onRemoveCoupon={onRemoveCoupon ?? noop}
          />
        )}

        {/* Ready to book confirmation banner */}
        {totalPrice >= 0 && bookingData.startTime && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.2 }}
          >
            <Box
              sx={{
                mt: 2,
                p: 2,
                bgcolor: alpha(theme.palette.success.main, 0.08),
                borderRadius: 2,
                border: `1px solid ${alpha(theme.palette.success.main, 0.25)}`,
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
              }}
            >
              <CheckCircleIcon sx={{ color: 'success.main', fontSize: 28, flexShrink: 0 }} />
              <Box>
                <Typography variant="body2" fontWeight={600} color="success.dark">
                  Your booking is ready!
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ opacity: 0.8 }}>
                  {bookingData.venueName && `${bookingData.venueName} • `}
                  {bookingData.date &&
                    new Date(bookingData.date).toLocaleDateString('en-IN', {
                      month: 'short',
                      day: 'numeric',
                    })}
                  {bookingData.startTime && ` at ${bookingData.startTime}`}
                  {bookingData.endTime && ` – ${bookingData.endTime}`}
                </Typography>
                <Typography variant="h6" fontWeight={700} color="success.dark" sx={{ mt: 0.5 }}>
                  Total: ₹{totalPrice.toLocaleString('en-IN')}
                </Typography>
              </Box>
            </Box>
          </motion.div>
        )}
      </Box>
    </motion.div>
  );
}
